/*
 * Courses page: training engagement dashboard.
 *
 * Headline KPIs (Courses Sent, Completion Rate, Pass Rate, Training Debt,
 * Course Lag), sends-vs-completions dual-bar chart, completion
 * breakdowns by type and department, score-distribution marimekko per
 * course, outstanding-users action card and a course send log preview.
 * Scope filters narrow every aggregate; period filter swaps the time
 * window.
 *
 * Calculations:
 *   - completionRate = completed / sent for the period (per course or org).
 *   - trainingDebt = courses with (sent > 14d ago AND completed_at IS NULL)
 *     divided by total sent; matches the formula in riskScore.js
 *     training-debt signal so KPI and risk score stay aligned.
 *   - courseLag = mean(days to complete), capped at 30 in score model.
 *   - passRate placeholder: users with score >= 60 / users completed. The
 *     threshold (60) is hard-coded and should be tenancy-configurable.
 *   - dual-bar chart: bucketSendVsCompletion(filteredLog, period) by month.
 *   - marimekko: per-course score-band distribution; column width = sends.
 *
 * Postgres mapping:
 *   - canonical `course_sends`, `courses` (id, name, type, version).
 *   - precomputed `course_period_metrics` (org_id, period, kpis_json)
 *     refreshed nightly so the page renders without recomputing.
 *   - outstanding view: WHERE completed_at IS NULL AND sent_at <
 *     NOW() - INTERVAL '14 days' GROUP BY email.
 *   - scope filters belong in the analytics endpoint WHERE clause.
 *
 * New data points:
 *   - course.type classification (5 buckets via COURSE_TYPE_MAP).
 *   - per-tenancy pass-rate threshold (replaces the hard-coded 60).
 *   - per-tenancy training-debt window (replaces the 14-day constant).
 *
 * Integration hook:
 *   - `coursesApi.useCoursesAnalyticsQuery({ period, scope })` returns
 *     { kpis, trends, byType, byDept, scoreDistribution, outstanding }.
 *   - cache TTL 30 minutes; invalidate on period/scope change.
 *
 * buildOutstandingStats is re-exported and consumed by
 * OutstandingUsersPage; the formula lives here to keep it in one place.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Grid, Typography, Button, LinearProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { COURSE_SEND_VS_COMPLETION_BY_MONTH, COURSE_USER_COMPLETIONS, COURSE_SEND_LOG, courseScoreRows, COURSE_TYPES, COURSES_BY_TYPE, DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES, USERS, DATA_EPOCH } from '../data';
import ActiveFilters from './ActiveFilters';
import DataTable, { RowViewButton } from './DataTable';
import WidgetState from './WidgetState';
import { buildCourseRows, courseRowsFromAgg } from './AllCoursesPage';
import { courseLogColumns } from './CourseSendHistoryPage';
import { TableHeader, TableRow } from './SimsPage';
import useDashboardData, { logInPeriod, periodWindowMs } from '../hooks/useDashboardData';
import { courseType, REMINDER_TRACKING_NOTE } from '../data';
import SectionTitle from './SectionTitle';
import { useCourseUsers, useCourseUsersQuery } from '../clickUsers';
import { EMPTY_CELL } from '../helpers';
import { serverRowsOrNull, toServerScope, hasTenancyScope } from '../serverScope';
import { useTenancyCourseEvents } from '../tenancyEvents';

// Last 5 unique courses by most recent send, with completion rates from the full log
const RECENT_COURSE_STATS = (() => {
    const seen = new Set();
    const recent = [];
    for (let i = COURSE_SEND_LOG.length - 1; i >= 0; i--) {
        const name = COURSE_SEND_LOG[i].courseName;
        if (!seen.has(name)) { seen.add(name); recent.push(name); }
        if (recent.length === 5) break;
    }
    return recent.map(name => {
        const entries = COURSE_SEND_LOG.filter(e => e.courseName === name);
        const sent = entries.length;
        const completed = entries.filter(e => e.dateCompleted).length;
        return { name, sent, completed, pct: Math.round((completed / sent) * 100) };
    });
})();

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Empty leading header cell that aligns with the per-row View button column.
function ViewTH() {
    return <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', width: '1px' }} />;
}

function MetricCard({ label, value, unit, sub, color, actionLabel, onAction }) {
    return (
        <motion.div variants={fadeUp}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2.5, flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>
                            {label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: color || 'text.primary' }}>
                                <CountUp end={parseFloat(String(value).replace(/,/g, ''))} duration={1.4} decimals={String(value).includes('.') ? 1 : 0} separator="," />
                            </Typography>
                            {unit && <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>{unit}</Typography>}
                        </Box>
                        {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.5 }}>{sub}</Typography>}
                    </Box>
                    {actionLabel && onAction && (
                        <Button
                            size="small" variant="outlined" color="secondary"
                            onClick={onAction}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                            sx={{
                                flexShrink: 0,
                                alignSelf: 'flex-end',
                                borderRadius: '6px', fontSize: 10, fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                px: 1.25, py: '3px',
                                borderColor: 'secondary.main', color: 'secondary.main',
                                '&:hover': { borderColor: 'secondary.main', backgroundColor: 'secondary.main', color: 'common.white' },
                                '& .MuiButton-endIcon': { transition: 'transform 200ms ease' },
                                '&:hover .MuiButton-endIcon': { transform: 'translateX(2px)' },
                            }}
                        >
                            {actionLabel}
                        </Button>
                    )}
                </Box>
            </MainCard>
        </motion.div>
    );
}

// Bucket helpers used when the chart needs to compute live from a scoped log.
// Same window as the server queries and the Simulations page.
function periodWindow(period, startDate, endDate) {
    return periodWindowMs({ period, startDate, endDate });
}
// Adaptive time-bucket granularity, matching the Simulations page date charts:
// 30 days or less buckets by day, over 30 days but under ~6 months by week
// (w/c labels), 6 months and up by month - real dates on the axis rather than
// W1/W2 or weekday names.
function bucketGranularity(winStart, winEnd) {
    const days = Math.round((winEnd - winStart) / 86400000);
    if (days <= 31) return 'day';
    if (days < 182) return 'week';
    return 'month';
}
function bucketKeyLabel(t, gran) {
    const d = new Date(t);
    if (gran === 'day') {
        return [d.toISOString().slice(0, 10), d.toLocaleString('en-GB', { day: 'numeric', month: 'short' })];
    }
    if (gran === 'week') {
        const monday = new Date(t - ((d.getUTCDay() + 6) % 7) * 86400000);
        return [monday.toISOString().slice(0, 10), `w/c ${monday.toLocaleString('en-GB', { day: 'numeric', month: 'short' })}`];
    }
    return [`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`, d.toLocaleString('en-GB', { month: 'short', year: '2-digit' })];
}
function bucketSendVsCompletion(log, period, startDate, endDate) {
    const [winStart, winEnd] = periodWindow(period, startDate, endDate);
    const gran = bucketGranularity(winStart, winEnd);
    const counts = new Map();
    for (const e of log) {
        const t = Date.parse(e.dateSent + 'T00:00:00Z');
        if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
        const [key, label] = bucketKeyLabel(t, gran);
        const cur = counts.get(key) || { key, label, sent: 0, completed: 0 };
        cur.sent += 1;
        if (e.dateCompleted) cur.completed += 1;
        counts.set(key, cur);
    }
    return [...counts.values()].sort((a, b) => a.key.localeCompare(b.key));
}

// Same bucketing as bucketSendVsCompletion, but over the precomputed daily
// course series (trends aggregate: { day, courseSends, courseCompleted }).
// Sums the daily aggregates instead of counting rows, so labels/x-axis match.
function bucketDailyCourse(series, period, startDate, endDate) {
    const [winStart, winEnd] = periodWindow(period, startDate, endDate);
    const gran = bucketGranularity(winStart, winEnd);
    const counts = new Map();
    for (const e of series) {
        const t = Date.parse(e.day + 'T00:00:00Z');
        if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
        const [key, label] = bucketKeyLabel(t, gran);
        const cur = counts.get(key) || { key, label, sent: 0, completed: 0 };
        cur.sent += (e.courseSends || 0);
        cur.completed += (e.courseCompleted || 0);
        counts.set(key, cur);
    }
    return [...counts.values()].sort((a, b) => a.key.localeCompare(b.key));
}

function CompletionsByMonthChart({ period, theme, log, trendsAgg, startDate, endDate }) {
    // When a scoped log is provided, recompute the buckets live so the chart
    // reflects the active filters. Otherwise fall back to the precomputed
    // module-level series for performance.
    const data = useMemo(
        () => Array.isArray(trendsAgg) ? bucketDailyCourse(trendsAgg, period, startDate, endDate) : (log ? bucketSendVsCompletion(log, period, startDate, endDate) : (COURSE_SEND_VS_COMPLETION_BY_MONTH[period] || COURSE_SEND_VS_COMPLETION_BY_MONTH['90d'])),
        [log, period, trendsAgg, startDate, endDate]
    );
    const isDark = theme.palette.mode === 'dark';
    const sentColor = theme.palette.secondary.main;
    const completedColor = theme.palette.primary.main;
    const tickColor = isDark ? '#5A6170' : '#A0A8B8';
    const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                    Sends vs Completions by Date
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Courses sent (left bar) vs completed (right bar) per period</Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, pt: 1, pb: 2, px: 1, overflow: 'hidden' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} barCategoryGap="12%" margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="4 4" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: tickColor, fontFamily: 'Inter' }} tickMargin={8} />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 10, fill: tickColor, fontFamily: 'Inter' }}
                            allowDecimals={false}
                            padding={{ top: 12 }}
                        />
                        <RechartsTooltip
                            cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`, borderRadius: 6, fontSize: 11, fontFamily: 'Inter', color: theme.palette.text.primary }}
                            itemStyle={{ color: theme.palette.text.primary }}
                            labelStyle={{ color: theme.palette.text.secondary, fontWeight: 600 }}
                        />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            iconType="circle"
                            iconSize={8}
                            wrapperStyle={{ fontSize: 10, fontFamily: 'Inter', top: 0, right: 8 }}
                        />
                        <Bar dataKey="sent"      name="Sent"      fill={sentColor}      radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive animationDuration={600} animationEasing="ease-out" />
                        <Bar dataKey="completed" name="Completed" fill={completedColor} radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive animationDuration={700} animationEasing="ease-out" />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </MainCard>
    );
}


function ShimmerBar({ color, opacity = 1, pct, delay, isDark }) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);
    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height: 24, borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    background: color,
                    opacity,
                    transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${delay}s`,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '-100%',
                        width: '100%', height: '100%',
                        background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)'}, transparent)`,
                        animation: value > 0 ? 'shine 2s ease-in-out' : 'none',
                        animationDelay: `${delay + 0.5}s`,
                        borderRadius: 1,
                    },
                },
                overflow: 'hidden',
                '@keyframes shine': { '0%': { left: '-100%' }, '100%': { left: '100%' } },
            }}
        />
    );
}

// ITEM_H / OVERHEAD kept for CompletionByGroupList
const ITEM_H = 54;
const OVERHEAD = 96;

// Concrete hex rather than the rbx CSS variables: MUI tooltips portal to
// <body>, outside .rbx-root where the variables live, so var() colours
// silently failed to resolve in the tooltip (3 of the 5 bands rendered
// unstyled there while matching the bars everywhere else). Values are the
// exact --rbx-green / --rbx-amber / --rbx-red definitions.
const SCORE_BANDS = [
    { label: '80-100', color: '#66BB6A' },
    { label: '60-79',  color: '#00BFA5' },
    { label: '40-59',  color: '#FFCA28' },
    { label: '20-39',  color: '#F97316' },
    { label: '0-19',   color: '#FF5252' },
];

const MARIMEKKO_PAGE_SIZE = 6;

// Small pagination control reused by both marimekko cards. Header-aligned
// so it doesn't compete with the chart body.
function MarimekkoPager({ page, pageCount, onChange }) {
    if (pageCount <= 1) return null;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Button
                size="small"
                onClick={() => onChange(Math.max(0, page - 1))}
                disabled={page === 0}
                sx={{ minWidth: 0, px: '6px', py: '2px', fontSize: 11, borderRadius: '4px', color: 'secondary.main', '&.Mui-disabled': { color: 'text.disabled' } }}
            >{'<'}</Button>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em', minWidth: 36, textAlign: 'center' }}>
                {page + 1}/{pageCount}
            </Typography>
            <Button
                size="small"
                onClick={() => onChange(Math.min(pageCount - 1, page + 1))}
                disabled={page >= pageCount - 1}
                sx={{ minWidth: 0, px: '6px', py: '2px', fontSize: 11, borderRadius: '4px', color: 'secondary.main', '&.Mui-disabled': { color: 'text.disabled' } }}
            >{'>'}</Button>
        </Box>
    );
}

function ScoreBreakdownMarimekko({ theme, coursesData }) {
    const allCourses = coursesData.filter((c) => c.sent > 0).map((c) => {
        const tot = (c.b80||0)+(c.b60||0)+(c.b40||0)+(c.b20||0)+(c.b0||0);
        const scores = tot === 0 ? [0,0,0,0,0] : [c.b80,c.b60,c.b40,c.b20,c.b0].map((b) => Math.round((b/tot)*100));
        return { name: c.courseName, sent: c.sent, scores, scaledSent: c.sent, bandCounts: [c.b80,c.b60,c.b40,c.b20,c.b0] };
    }).sort((a, b) => b.sent - a.sent);

    const [page, setPage] = useState(0);
    const pageCount = Math.max(1, Math.ceil(allCourses.length / MARIMEKKO_PAGE_SIZE));
    const courses = allCourses.slice(page * MARIMEKKO_PAGE_SIZE, page * MARIMEKKO_PAGE_SIZE + MARIMEKKO_PAGE_SIZE);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Score Breakdown by Course
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Showing {courses.length} of {allCourses.length} - column width = sends - height = score distribution
                    </Typography>
                </Box>
                <MarimekkoPager page={page} pageCount={pageCount} onChange={setPage} />
            </Box>

            {/* Legend */}
            <Box sx={{ px: 3, pt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
                {SCORE_BANDS.map(b => (
                    <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{b.label}</Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ px: 3, pt: 1.5, pb: 2.5, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: '3px', height: 200, minWidth: 0 }}>
                        {courses.map((c) => (
                            <Tooltip
                                key={c.name}
                                placement="top"
                                arrow
                                title={
                                    <Box sx={{ minWidth: 140 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>{c.name}</Typography>
                                        <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.75 }}>
                                            {c.bandCounts.reduce((a, n) => a + n, 0)} scored of {c.scaledSent} sends
                                        </Typography>
                                        {SCORE_BANDS.map((b, bi) => (
                                            <Box key={b.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 10 }}>
                                                <Box component="span" sx={{ color: b.color, fontWeight: 600 }}>{b.label}</Box>
                                                <Box component="span" sx={{ color: 'text.primary' }}>{c.bandCounts[bi]} ({c.scores[bi]}%)</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                }
                            >
                                <Box
                                    sx={{
                                        flex: c.scaledSent,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        cursor: 'default',
                                        transition: 'filter 150ms',
                                        '&:hover': { filter: 'brightness(1.1)' },
                                    }}
                                >
                                    {SCORE_BANDS.map((b, bi) => (
                                        <Box
                                            key={b.label}
                                            sx={{
                                                flex: c.bandCounts[bi],
                                                bgcolor: b.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                            }}
                                        >
                                        </Box>
                                    ))}
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>

                    {/* X-axis labels */}
                    <Box sx={{ pt: '6px', display: 'flex', gap: '3px' }}>
                        {courses.map((c) => (
                            <Box
                                key={c.name}
                                sx={{ flex: c.scaledSent, overflow: 'hidden', minWidth: 0 }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {c.name}
                                </Typography>
                                <Typography sx={{ fontSize: 9, fontWeight: 500, color: 'text.secondary' }}>
                                    {c.scaledSent}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
        </MainCard>
    );
}

function IndividualCompletionTable({ drill }) {
    const TH = ({ children }) => (
        <Box component="th" sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled', px: 2, py: 1.5, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
            {children}
        </Box>
    );

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Completion by Individual
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Click View for user detail</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{REMINDER_TRACKING_NOTE}</Typography>
                </Box>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{COURSE_USER_COMPLETIONS.length} users</Typography>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <ViewTH /><TH>Name</TH><TH>Email</TH><TH>Department</TH><TH>Location</TH><TH>Completed</TH><TH>Sent</TH><TH>Reminders</TH><TH>Rate</TH>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {COURSE_USER_COMPLETIONS.map((u) => (
                                <Box
                                    component="tr" key={u.email}
                                    sx={{
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                    }}
                                >
                                    <Box component="td" sx={{ px: 2, py: 1.5, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => drill('user-detail', `${u.firstName} ${u.lastName}`, { type: 'user', value: u.email })} />
                                    </Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>{u.firstName} {u.lastName}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary', fontSize: 11 }}>{u.email}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.dept}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.location}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.primary', fontWeight: 600 }}>{u.completed}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.sent}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.reminders}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, minWidth: 120 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1, height: 5, borderRadius: 2, bgcolor: 'divider', overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${u.pct}%`, borderRadius: 2, bgcolor: u.pct >= 80 ? 'var(--green)' : u.pct >= 50 ? 'var(--amber)' : 'var(--red)' }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: u.pct >= 80 ? 'var(--green)' : u.pct >= 50 ? 'var(--amber)' : 'var(--red)', width: 32, textAlign: 'right' }}>
                                                {u.pct}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}

// -- Completion-rate RAG --------------------------------------------------
// Absolute thresholds, not tenancy-relative: a 70% completion rate should
// never be flagged red just because other groups happen to be higher.
// The fill is interpolated through a fixed palette of hex stops so the
// colour eases smoothly across the whole 0-100 range:
//   100 hard green -> 75 soft green -> 74 soft amber -> 50 hard amber
//                  -> 49 soft red   -> 0  hard red
// Every band boundary is soft on both sides, the two extremes (100 and 0)
// are the only places the fill reads at full strength.
// Traffic-light RAG, based on Material Design 500 anchors so the colours
// read like a road sign rather than a saturated neon. Each band runs from
// a softened tint at the inner boundary to a brighter 500-tone at the
// extreme, so the gradient eases through neutral territory at every band
// edge.
const RAG_GRADIENT_STOPS = [
    { at: 0,   hex: '#FF5252' }, // traffic red     (Red 500)
    { at: 25,  hex: '#E57373' }, //                 (Red 300)
    { at: 49,  hex: '#FFEBEE' }, // soft pink boundary
    { at: 50,  hex: '#FFCA28' }, // traffic amber   (Amber 500)
    { at: 62,  hex: '#FFD54F' }, //                 (Amber 300)
    { at: 74,  hex: '#FFF8E1' }, // soft cream boundary
    { at: 75,  hex: '#E8F5E9' }, // soft mint boundary
    { at: 87,  hex: '#81C784' }, //                 (Green 300)
    { at: 100, hex: '#66BB6A' }, // traffic green   (Green 500)
];
function hexToRgb(hex) {
    const m = hex.replace('#', '');
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
}
function lerpHex(a, b, t) {
    const [r1, g1, b1] = hexToRgb(a);
    const [r2, g2, b2] = hexToRgb(b);
    return `rgb(${Math.round(r1 + (r2 - r1) * t)},${Math.round(g1 + (g2 - g1) * t)},${Math.round(b1 + (b2 - b1) * t)})`;
}
function completionBand(pct) {
    if (pct == null) return null;
    const stops = RAG_GRADIENT_STOPS;
    let bg;
    if (pct <= stops[0].at) bg = stops[0].hex;
    else if (pct >= stops[stops.length - 1].at) bg = stops[stops.length - 1].hex;
    else {
        for (let i = 0; i < stops.length - 1; i++) {
            const a = stops[i];
            const b = stops[i + 1];
            if (pct >= a.at && pct <= b.at) {
                bg = lerpHex(a.hex, b.hex, (pct - a.at) / (b.at - a.at));
                break;
            }
        }
    }
    // Text colour switches with the band, with white kicking in on the
    // most saturated red where deep-tone text would drop below AA contrast.
    let text;
    if (pct >= 75) text = '#388E3C';
    else if (pct >= 50) text = '#C68A00';
    else if (pct >= 20) text = '#E53935';
    else text = '#FFFFFF';
    return { bg, text };
}

// -- Completion Rate by Group, picker-driven heatmap tile grid ---------
// Tiles per chosen grouping. Org mode collapses to a single tile showing
// the org-wide completion %; this replaces the standalone "Completion by
// Course" card by making the same overall-roll-up an option of this widget.
// Clicking any tile pushes a scope filter onto the page so the rest of
// the dashboard re-narrows to that group.
const GROUPING_OPTIONS = ['Org', 'Department', 'Location', 'City', 'Country', 'Company', 'State'];
const GROUP_FIELD_BY_KEY = { Department: 'dept', Location: 'location', City: 'city', Country: 'country', Company: 'company', State: 'state' };
const GROUP_SCOPE_TYPE   = { Department: 'department', Location: 'location', City: 'city', Country: 'country', Company: 'company', State: 'state' };

function CompletionByDeptChart({ log = COURSE_SEND_LOG, drill, deptAgg, orgAgg, useAgg, serverScope, scope, removeScope, period, startDate, endDate }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const secondary = theme.palette.secondary.main;
    const [group, setGroup] = useState('Department');
    const [anchorEl, setAnchorEl] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const dimType = GROUP_SCOPE_TYPE[group];
    const aggDims = useAgg && ['location', 'city', 'country', 'company', 'state'].includes(dimType);
    const dimsQ = useDashboardData({ metric: 'courses', aggregation: 'course_dims', groupBy: dimType, period, startDate, endDate, scope: serverScope }, { skip: !aggDims });
    const dimRows = aggDims ? dimsQ.data?.courseDims : null;

    const data = useMemo(() => {
        // Department view (unscoped) is served by the precomputed, period-windowed
        // course_detail.byDept aggregate (uncapped). Other groupings / scoped views
        // fall back to the row computation below.
        if (group === 'Department' && Array.isArray(deptAgg)) {
            return deptAgg
                .map((d) => ({ name: d.name, sent: d.sent, completed: d.completed, pct: d.sent === 0 ? null : d.pct }))
                .sort((a, b) => (b.sent - a.sent) || ((b.pct ?? -1) - (a.pct ?? -1)));
        }
        if (Array.isArray(dimRows)) {
            const map = {};
            dimRows.forEach((r) => { if (!map[r.dimValue]) map[r.dimValue] = { name: r.dimValue, sent: 0, completed: 0 }; map[r.dimValue].sent += r.sent; map[r.dimValue].completed += r.completed; });
            return Object.values(map).map((d) => ({ ...d, pct: d.sent === 0 ? null : Math.round((d.completed / d.sent) * 100) })).sort((a, b) => (b.sent - a.sent) || ((b.pct ?? -1) - (a.pct ?? -1)));
        }
        if (group === 'Org') {
            const sent = orgAgg ? orgAgg.sent : log.length;
            const completed = orgAgg ? orgAgg.completed : log.filter((r) => r.dateCompleted).length;
            return [{
                name: 'Organisation',
                sent,
                completed,
                pct: sent === 0 ? null : Math.round((completed / sent) * 100),
                org: true,
            }];
        }
        const field = GROUP_FIELD_BY_KEY[group] || 'dept';
        const map = {};
        log.forEach((r) => {
            const k = r[field] || 'Unknown';
            if (!map[k]) map[k] = { name: k, sent: 0, completed: 0 };
            map[k].sent += 1;
            if (r.dateCompleted) map[k].completed += 1;
        });
        return Object.values(map)
            .map((d) => ({ ...d, pct: d.sent === 0 ? null : Math.round((d.completed / d.sent) * 100) }))
            .sort((a, b) => (b.sent - a.sent) || ((b.pct ?? -1) - (a.pct ?? -1)));
    }, [log, group, deptAgg, orgAgg, dimRows]);

    // Absolute RAG thresholds (>=75 green, 50-74 amber, <50 red). Org mode
    // keeps the secondary tint since a single tile is its own scale.
    const cellColor = (pct, org) => {
        if (pct == null) return isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
        if (org) return alpha(secondary, 0.15 + (pct / 100) * 0.75);
        return completionBand(pct).bg;
    };
    const cellTextColor = (pct, org) => {
        if (pct == null) return 'text.disabled';
        if (org) return pct >= 55 ? '#fff' : 'text.primary';
        return completionBand(pct).text;
    };

    // Values already in scope read as "selected"; clicking one clears it again
    // so the chart toggles its own filter rather than re-applying it.
    const scopeValues = (type) => { const v = (scope || {})[type]; return v == null ? [] : Array.isArray(v) ? v : [v]; };
    const isFiltered = (name) => { const t = GROUP_SCOPE_TYPE[group]; return !!t && scopeValues(t).includes(name); };
    const handleTileClick = (tile) => {
        if (!drill || tile.org) return;
        const scopeType = GROUP_SCOPE_TYPE[group];
        if (!scopeType) return;
        if (scopeValues(scopeType).includes(tile.name)) { removeScope && removeScope(scopeType, tile.name); return; }
        // Re-drill into Courses with the scope context, ActiveFilters picks
        // it up and the rest of the page narrows to match.
        drill('courses', `Courses - ${tile.name}`, { type: scopeType, value: tile.name });
    };

    const isOrg = group === 'Org';
    const CAP = 24;
    const many = !isOrg && data.length > CAP;
    const visible = !many || showAll ? data : data.slice(0, CAP);

    return (
        <WidgetState queries={[dimsQ]} fill>
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        {isOrg ? 'Completion Rate - Organisation' : `Completion Rate by ${group}`}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        {isOrg ? 'Roll-up across the whole org for the scoped log' : 'Green >=75% - Amber 50-74% - Red <50% - click to filter'}
                    </Typography>
                </Box>
                <>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{
                            borderRadius: '4px',
                            borderColor: 'secondary.main',
                            color: 'secondary.main',
                            fontSize: 11,
                            py: '3px',
                            px: '10px',
                            textTransform: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: 'secondary.main', borderColor: 'secondary.main', color: 'common.white' },
                        }}
                    >
                        {group}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{ sx: { border: '1px solid', borderColor: 'secondary.main', borderRadius: '4px', boxShadow: 'none', mt: 0.5 } }}
                    >
                        {GROUPING_OPTIONS.map((o) => (
                            <MenuItem
                                key={o}
                                onClick={() => { setGroup(o); setAnchorEl(null); }}
                                sx={{
                                    fontSize: 11,
                                    borderRadius: '4px', mx: 0.5,
                                    color: o === group ? 'common.white' : 'secondary.main',
                                    backgroundColor: o === group ? 'secondary.main' : 'transparent',
                                    '&:hover': { backgroundColor: 'secondary.main', color: 'common.white' },
                                }}
                            >
                                {o}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            </Box>
            <Box sx={{ flex: 1, p: 3, pt: 2, display: 'flex' }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isOrg ? '1fr' : 'repeat(4, minmax(0, 1fr))',
                    gap: '6px', width: '100%', alignContent: 'stretch',
                    ...(showAll && many ? { maxHeight: 380, overflowY: 'auto' } : {}),
                }}>
                    {visible.map((d) => (
                        <Tooltip
                            key={d.name}
                            arrow
                            placement="top"
                            title={
                                <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{d.name}</Typography>
                                    <Typography sx={{ fontSize: 11 }}>{d.pct == null ? 'No sends' : `${d.completed}/${d.sent} completed (${d.pct}%)`}</Typography>
                                    {!d.org && drill && <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.5 }}>Click to filter</Typography>}
                                </Box>
                            }
                        >
                            <Box
                                onClick={() => handleTileClick(d)}
                                sx={{
                                    bgcolor: cellColor(d.pct, d.org),
                                    borderRadius: '6px',
                                    p: isOrg ? 2 : 1,
                                    minHeight: isOrg ? 110 : 56,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    color: cellTextColor(d.pct, d.org),
                                    cursor: d.org || !drill ? 'default' : 'pointer',
                                    transition: 'filter 150ms, transform 150ms',
                                    '&:hover': { filter: 'brightness(1.08)', transform: d.org ? 'none' : 'translateY(-1px)' },
                                }}
                            >
                                <Typography sx={{ fontSize: isOrg ? 12 : 10, fontWeight: 600, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {d.name}
                                </Typography>
                                <Typography sx={{ fontSize: isOrg ? 36 : 16, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' }}>
                                    {d.pct == null ? EMPTY_CELL : `${d.pct}%`}
                                </Typography>
                                {isOrg && (
                                    <Typography sx={{ fontSize: 11, opacity: 0.8 }}>
                                        {d.completed.toLocaleString()} of {d.sent.toLocaleString()} completed
                                    </Typography>
                                )}
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
            {many && (
                <Box sx={{ px: 3, pb: 2, pt: 0.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {showAll ? `All ${data.length}` : `Top ${CAP}`} of {data.length} {group.toLowerCase()}s by volume
                    </Typography>
                    <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ fontSize: 10, py: '2px', px: 1, minWidth: 0, textTransform: 'none', color: 'secondary.main', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                        {showAll ? 'Show top 24' : 'Show all'}
                    </Button>
                </Box>
            )}
        </MainCard>
        </WidgetState>
    );
}

// -- Course Lag swing, sorted-ramp area with avg reference line ------------
// Every completion in scope is plotted by its delivery-to-complete delay
// (capped at 30d to match the riskScore Course Lag signal), sorted
// ascending. The ramp shows how the org distributes between fast and
// slow completers; the dashed line marks the average so the eye can
// read where the bulk sits relative to the headline KPI.
function CourseLagChart({ log = COURSE_SEND_LOG, lagAgg }) {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const LAG_CAP = 30;

    const { data, avg, p50, p90, n, yMax, yTicks } = useMemo(() => {
        if (Array.isArray(lagAgg)) {
            const bins = [...lagAgg].sort((a, b) => a.lagDay - b.lagDay);
            const N = bins.reduce((s, b) => s + b.n, 0);
            if (N === 0) return { data: [], avg: 0, p50: 0, p90: 0, n: 0, yMax: LAG_CAP, yTicks: [0, 7, 14, 21, 30] };
            const total = bins.reduce((s, b) => s + b.lagDay * b.n, 0);
            const lagAtQ = (q) => { const target = q * (N - 1); let cum = 0; for (const b of bins) { if (cum + b.n > target) return b.lagDay; cum += b.n; } return bins[bins.length - 1].lagDay; };
            const maxLag = bins[bins.length - 1].lagDay;
            const ceiling = Math.min(LAG_CAP, Math.max(1, Math.ceil(maxLag + 1)));
            const step = ceiling <= 8 ? 2 : ceiling <= 16 ? 4 : ceiling <= 24 ? 6 : 10;
            const ticks = []; for (let v = 0; v <= ceiling; v += step) ticks.push(v); if (ticks[ticks.length - 1] !== ceiling) ticks.push(ceiling);
            const SAMPLES = 120; const ramp = []; for (let i = 0; i < SAMPLES; i++) { const q = i / (SAMPLES - 1); ramp.push({ x: q * 100, lag: lagAtQ(q) }); }
            return { data: ramp, avg: total / N, p50: lagAtQ(0.5), p90: lagAtQ(0.9), n: N, yMax: ceiling, yTicks: ticks };
        }
        const lags = [];
        for (const r of log) {
            if (!r.dateCompleted) continue;
            const sentMs = Date.parse(r.dateSent + 'T00:00:00Z');
            const doneMs = Date.parse(r.dateCompleted + 'T00:00:00Z');
            lags.push(Math.min(LAG_CAP, Math.max(0, (doneMs - sentMs) / 86400000)));
        }
        if (lags.length === 0) return { data: [], avg: 0, p50: 0, p90: 0, n: 0, yMax: LAG_CAP, yTicks: [0, 7, 14, 21, 30] };
        lags.sort((a, b) => a - b);
        const total = lags.reduce((a, b) => a + b, 0);
        const pointAt = (q) => lags[Math.min(lags.length - 1, Math.floor((lags.length - 1) * q))];
        const maxLag = lags[lags.length - 1];
        const ceiling = Math.min(LAG_CAP, Math.max(1, Math.ceil(maxLag + 1)));
        const step = ceiling <= 8 ? 2 : ceiling <= 16 ? 4 : ceiling <= 24 ? 6 : 10;
        const ticks = [];
        for (let v = 0; v <= ceiling; v += step) ticks.push(v);
        if (ticks[ticks.length - 1] !== ceiling) ticks.push(ceiling);
        return {
            data: lags.map((d, i) => ({ x: (i / Math.max(1, lags.length - 1)) * 100, lag: d })),
            avg: total / lags.length,
            p50: pointAt(0.5),
            p90: pointAt(0.9),
            n: lags.length,
            yMax: ceiling,
            yTicks: ticks,
        };
    }, [log, lagAgg]);

    if (n === 0) return null;

    const isDark = theme.palette.mode === 'dark';
    const grid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tick = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
    const refStroke = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexShrink: 0 }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                            Course Lag
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Every completion sorted by delivery-to-complete delay. Steeper tail = more slow finishers.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2.5, flexShrink: 0, ml: 2 }}>
                        {[
                            { label: 'Slowest 10%', value: p90 },
                            { label: 'Average',     value: avg },
                        ].map((s) => (
                            <Box key={s.label} sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                                    {s.label}
                                </Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.25 }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1 }}>
                                        <CountUp end={s.value} duration={1.1} decimals={1} />
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, fontWeight: 500, color: 'text.secondary' }}>d</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 16, right: 20, left: 4, bottom: 4 }}>
                            <defs>
                                <linearGradient id="lagFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={primary} stopOpacity={0.4} />
                                    <stop offset="100%" stopColor={primary} stopOpacity={0.04} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={grid} vertical={false} />
                            <XAxis
                                dataKey="x"
                                type="number"
                                domain={[0, 100]}
                                ticks={[0, 25, 50, 75, 100]}
                                tickFormatter={(v) => `${v}%`}
                                stroke={tick}
                                tick={{ fontSize: 10, fill: tick }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                domain={[0, yMax]}
                                ticks={yTicks}
                                tickFormatter={(v) => `${v}d`}
                                stroke={tick}
                                tick={{ fontSize: 10, fill: tick }}
                                tickLine={false}
                                axisLine={false}
                                width={36}
                            />
                            <RechartsTooltip
                                cursor={{ stroke: primary, strokeDasharray: '3 3', strokeOpacity: 0.5 }}
                                contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 6, fontSize: 11 }}
                                formatter={(value) => [`${Number(value).toFixed(1)} days`, 'Lag']}
                                labelFormatter={(v) => `${Number(v).toFixed(0)}% of completions`}
                            />
                            <Area
                                type="basis"
                                dataKey="lag"
                                stroke={primary}
                                strokeWidth={2}
                                fill="url(#lagFill)"
                                dot={false}
                                isAnimationActive
                                animationDuration={900}
                            />
                            <Line
                                type="basis"
                                dataKey={() => avg}
                                stroke={refStroke}
                                strokeWidth={1.5}
                                strokeDasharray="4 4"
                                dot={false}
                                isAnimationActive={false}
                                legendType="none"
                                name="Average"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>

                <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 1 }}>
                    n = {n.toLocaleString()} completions in scope · capped at {LAG_CAP}d to match the Course lag score driver
                </Typography>
            </Box>
        </MainCard>
    );
}

// -- Score Breakdown by Course Type (Marimekko, aggregated) -----------------
// Reuses SCORE_BANDS visual; column width = type's total sends, segments are
// the average of each course's score band weighted by its sends.
function ScoreBreakdownByTypeMarimekko({ coursesData }) {
    const allTypes = COURSE_TYPES.map((typeName) => {
        // Sum the real per-course band counts (each band is a count of scored
        // completions). Previously the bands were re-derived by scaling each
        // course's band PERCENTAGES by its SENDS, which produced more
        // "completions" than sends, and a Math.max(1,...) floor added one more
        // per empty band on top.
        const bandSums = [0, 0, 0, 0, 0];
        let totalSent = 0;
        for (const c of coursesData.filter((x) => courseType(x.courseName) === typeName)) {
            totalSent += c.sent || 0;
            [c.b80, c.b60, c.b40, c.b20, c.b0].forEach((b, i) => { bandSums[i] += b || 0; });
        }
        const completed = bandSums.reduce((a, b) => a + b, 0);
        const scores = bandSums.map((b) => (completed === 0 ? 0 : Math.round((b / completed) * 100)));
        return { name: typeName, sent: Math.max(1, totalSent), completed, scores, bandCounts: bandSums };
    });

    const [page, setPage] = useState(0);
    const pageCount = Math.max(1, Math.ceil(allTypes.length / MARIMEKKO_PAGE_SIZE));
    const types = allTypes.slice(page * MARIMEKKO_PAGE_SIZE, page * MARIMEKKO_PAGE_SIZE + MARIMEKKO_PAGE_SIZE);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Score Breakdown by Course Type
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Showing {types.length} of {allTypes.length} - column width = sends per type - height = score distribution
                    </Typography>
                </Box>
                <MarimekkoPager page={page} pageCount={pageCount} onChange={setPage} />
            </Box>
            <Box sx={{ px: 3, pt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
                {SCORE_BANDS.map((b) => (
                    <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{b.label}</Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
                <Box sx={{ display: 'flex', gap: '4px', height: 200 }}>
                    {types.map((t) => (
                        <Tooltip
                            key={t.name}
                            placement="top"
                            arrow
                            title={
                                <Box sx={{ minWidth: 160 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>{t.name}</Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.75 }}>
                                        {t.completed} scored of {t.sent} sends
                                    </Typography>
                                    {SCORE_BANDS.map((b, bi) => (
                                        <Box key={b.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 10 }}>
                                            <Box component="span" sx={{ color: b.color, fontWeight: 600 }}>{b.label}</Box>
                                            <Box component="span" sx={{ color: 'text.primary' }}>{t.bandCounts[bi]} ({t.scores[bi]}%)</Box>
                                        </Box>
                                    ))}
                                </Box>
                            }
                        >
                            <Box sx={{ flex: t.sent, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: '5px', overflow: 'hidden', cursor: 'default', transition: 'filter 150ms', '&:hover': { filter: 'brightness(1.1)' } }}>
                                {SCORE_BANDS.map((b, bi) => (
                                    <Box key={b.label} sx={{ flex: t.bandCounts[bi], bgcolor: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', px: 0.5 }} />
                                ))}
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
            <Box sx={{ px: 3, pt: '6px', pb: 2.5, display: 'flex', gap: '4px' }}>
                {types.map((t) => (
                    <Box key={t.name} sx={{ flex: t.sent, overflow: 'hidden', minWidth: 0 }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>{t.name}</Typography>
                        <Typography sx={{ fontSize: 9, fontWeight: 500, color: 'text.secondary' }}>{t.sent}</Typography>
                    </Box>
                ))}
            </Box>
        </MainCard>
    );
}

// -- Department by Course Type Heatmap -------------------------------------
// Rows are course types, columns are departments. Cell value is the
// completion rate for that (type, dept) pair.
// Maps the user-facing label of the meta dimension to the field name on the
// COURSE_SEND_LOG events and the catalogue used to seed the row list.
const META_OPTIONS = [
    // Live-binding getters: refresh after each data hydration.
    { label: 'Department', field: 'dept',     get source() { return DEPTS; } },
    { label: 'Location',   field: 'location', get source() { return LOCS; } },
    { label: 'City',       field: 'city',     get source() { return CITIES; } },
    { label: 'Country',    field: 'country',  get source() { return COUNTRIES; } },
    { label: 'Company',    field: 'company',  get source() { return COMPANIES; } },
    { label: 'State',      field: 'state',    get source() { return STATES; } },
];

const META_SCOPE_TYPE = { Department: 'department', Location: 'location', City: 'city', Country: 'country', Company: 'company', State: 'state' };

function DeptByTypeHeatmap({ log = COURSE_SEND_LOG, drill, useAgg, serverScope, scope, removeScope, period, startDate, endDate }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [metaIdx, setMetaIdx] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showAllRows, setShowAllRows] = useState(false);
    const meta = META_OPTIONS[metaIdx];
    const dimType = META_SCOPE_TYPE[meta.label];
    const aggDims = useAgg && ['department', 'location', 'city', 'country', 'company', 'state'].includes(dimType);
    const dimsQ = useDashboardData({ metric: 'courses', aggregation: 'course_dims', groupBy: dimType, period, startDate, endDate, scope: serverScope }, { skip: !aggDims });
    const dimRows = aggDims ? serverRowsOrNull(dimsQ.data?.courseDims, scope) : null;

    // Only include rows / columns that have at least one send so the heatmap
    // never renders empty rows or columns.
    let rows, types, matrix;
    if (Array.isArray(dimRows)) {
        const byRow = {};
        for (const r of dimRows) { if (!byRow[r.dimValue]) byRow[r.dimValue] = {}; byRow[r.dimValue][r.courseType] = { sent: r.sent, completed: r.completed }; }
        const typeSet = new Set(dimRows.map((r) => r.courseType));
        types = COURSE_TYPES.filter((t) => typeSet.has(t));
        rows = Object.keys(byRow).sort((a, b) => { const sa = Object.values(byRow[a]).reduce((x, c) => x + c.sent, 0); const sb = Object.values(byRow[b]).reduce((x, c) => x + c.sent, 0); return sb - sa; });
        matrix = rows.map((rowName) => ({ row: rowName, cells: types.map((typeName) => { const c = byRow[rowName][typeName]; const sent = c ? c.sent : 0; const completed = c ? c.completed : 0; return { type: typeName, sent, completed, pct: sent === 0 ? null : Math.round((completed / sent) * 100) }; }) }));
    } else {
        const allRows = meta.source.map((r) => r.name);
        const rowHasHistory = new Set(log.map((e) => e[meta.field]));
        const typeHasHistory = new Set(log.map((e) => e.courseType));
        rows = allRows.filter((r) => rowHasHistory.has(r));
        types = COURSE_TYPES.filter((t) => typeHasHistory.has(t));
        matrix = rows.map((rowName) => { const cells = types.map((typeName) => { const events = log.filter((e) => e.courseType === typeName && e[meta.field] === rowName); const sent = events.length; const completed = events.filter((e) => e.dateCompleted).length; return { type: typeName, sent, completed, pct: sent === 0 ? null : Math.round((completed / sent) * 100) }; }); return { row: rowName, cells }; });
    }

    if (matrix.length === 0 || rows.length === 0) {
        return (
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>
                    No course history yet
                </Box>
            </MainCard>
        );
    }

    // Absolute RAG thresholds (>=75 green, 50-74 amber, <50 red) so the colour
    // means the same thing across every intersection regardless of the
    // surrounding distribution.
    const cellColor = (pct) => {
        if (pct == null) return isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
        return completionBand(pct).bg;
    };
    const cellTextColor = (pct) => {
        if (pct == null) return 'text.disabled';
        return completionBand(pct).text;
    };

    const ROWCAP = 15;
    const manyRows = rows.length > ROWCAP;
    const shownMatrix = !manyRows || showAllRows ? matrix : matrix.slice(0, ROWCAP);

    return (
        <WidgetState queries={[dimsQ]} fill>
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                        Completion Rate by {meta.label} by Course Type
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Green >=75% - Amber 50-74% - Red &lt;50%</Typography>
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        borderRadius: '4px',
                        borderColor: 'secondary.main',
                        color: 'secondary.main',
                        fontSize: 11,
                        py: '3px',
                        px: '10px',
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'secondary.main', borderColor: 'secondary.main', color: 'common.white' },
                    }}
                >
                    {meta.label}
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{ sx: { border: '1px solid', borderColor: 'secondary.main', borderRadius: '4px', boxShadow: 'none', mt: 0.5 } }}
                >
                    {META_OPTIONS.map((opt, i) => (
                        <MenuItem
                            key={opt.label}
                            onClick={() => { setMetaIdx(i); setAnchorEl(null); }}
                            sx={{
                                fontSize: 11,
                                borderRadius: '4px',
                                mx: 0.5,
                                color: i === metaIdx ? 'common.white' : 'secondary.main',
                                backgroundColor: i === metaIdx ? 'secondary.main' : 'transparent',
                                '&:hover': { backgroundColor: 'secondary.main', color: 'common.white' },
                            }}
                        >
                            {opt.label}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
            <Box sx={{ p: 3, overflowX: 'auto', ...(showAllRows && manyRows ? { maxHeight: 460, overflowY: 'auto' } : {}) }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `120px repeat(${types.length}, minmax(0, 1fr))`, gap: '4px', alignItems: 'stretch', width: '100%' }}>
                    {/* Header row, course type names, wrap to multiple lines so
                        long labels like "Information Security" stay readable. */}
                    <Box />
                    {types.map((t) => (
                        <Box
                            key={t}
                            sx={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: 'text.disabled',
                                textAlign: 'center',
                                pb: 1,
                                px: 0.5,
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                                wordBreak: 'normal',
                                overflowWrap: 'break-word',
                                hyphens: 'auto',
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                            }}
                        >
                            {t}
                        </Box>
                    ))}
                    {/* Rows for the selected meta dimension */}
                    {shownMatrix.map((row) => {
                        const scopeType = META_SCOPE_TYPE[meta.label];
                        const canFilter = !!drill && !!scopeType;
                        return (
                        <React.Fragment key={row.row}>
                            <Box
                                onClick={canFilter ? () => {
                                    const cur = (scope || {})[scopeType];
                                    const vals = cur == null ? [] : Array.isArray(cur) ? cur : [cur];
                                    // Clicking a row that is already filtering the page clears it.
                                    if (vals.includes(row.row)) { removeScope && removeScope(scopeType, row.row); return; }
                                    drill('courses', `Courses - ${row.row}`, { type: scopeType, value: row.row });
                                } : undefined}
                                sx={{
                                    display: 'flex', alignItems: 'center',
                                    fontSize: 11, fontWeight: 600, color: 'text.primary', pr: 1.5,
                                    cursor: canFilter ? 'pointer' : 'default',
                                    transition: 'color 150ms',
                                    '&:hover': canFilter ? { color: 'secondary.main' } : {},
                                }}
                                title={canFilter ? `Filter by ${row.row}` : undefined}
                            >
                                {row.row}
                            </Box>
                            {row.cells.map((c) => (
                                <Tooltip
                                    key={`${row.row}-${c.type}`}
                                    arrow
                                    placement="top"
                                    title={
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{row.row}</Typography>
                                            <Typography sx={{ fontSize: 11 }}>{c.type}</Typography>
                                            <Typography sx={{ fontSize: 11 }}>{c.pct == null ? 'No sends' : `${c.completed}/${c.sent} completed (${c.pct}%)`}</Typography>
                                        </Box>
                                    }
                                >
                                    <Box sx={{
                                        bgcolor: cellColor(c.pct),
                                        borderRadius: '4px',
                                        py: 1.5, px: 1,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 11, fontWeight: 700,
                                        color: cellTextColor(c.pct),
                                        cursor: 'default',
                                        transition: 'filter 150ms',
                                        '&:hover': { filter: 'brightness(1.08)' },
                                    }}>
                                        {c.pct == null ? EMPTY_CELL : `${c.pct}%`}
                                    </Box>
                                </Tooltip>
                            ))}
                        </React.Fragment>
                        );
                    })}
                </Box>
            </Box>
            {manyRows && (
                <Box sx={{ px: 3, pb: 2, pt: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {showAllRows ? `All ${rows.length}` : `Top ${ROWCAP}`} of {rows.length} {meta.label.toLowerCase()}s by volume
                    </Typography>
                    <Button size="small" onClick={() => setShowAllRows((v) => !v)} sx={{ fontSize: 10, py: '2px', px: 1, minWidth: 0, textTransform: 'none', color: 'secondary.main', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                        {showAllRows ? 'Show top 15' : 'Show all'}
                    </Button>
                </Box>
            )}
        </MainCard>
        </WidgetState>
    );
}

// Aggregate the (possibly scope/search-filtered) log into per-user
// outstanding stats. Used by both the preview card and the full-page table.
export function buildOutstandingStats(log) {
    const map = {};
    for (const e of log) {
        const key = e.email || `${e.firstName} ${e.lastName}`;
        if (!map[key]) {
            map[key] = {
                email: e.email,
                name: `${e.firstName} ${e.lastName}`,
                dept: e.dept,
                location: e.location,
                sent: 0,
                outstanding: 0,
                oldestDays: 0,
            };
        }
        const s = map[key];
        s.sent += 1;
        if (!e.dateCompleted) {
            s.outstanding += 1;
            const ageDays = Math.max(0, Math.floor((Date.now() - Date.parse(e.dateSent + 'T00:00:00Z')) / 86400000));
            if (ageDays > s.oldestDays) s.oldestDays = ageDays;
        }
    }
    return Object.values(map)
        .filter((u) => u.outstanding > 0)
        .sort((a, b) => b.outstanding - a.outstanding || b.oldestDays - a.oldestDays);
}

// Score KPIs over a send log. Avg Score is the mean over every scored attempt,
// completed or not, so retries and in-flight attempts count. Pass Rate is the
// share of completed AND scored attempts hitting the 80% pass mark (unscored
// completions are excluded so courses without a quiz can't drag it down), and
// Avg Pass Score is the mean score within that passing cohort.
function filterCourseLog(log, scope, search) {
    const s = scope || {};
    const q = search.trim().toLowerCase();
    const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };
    return log.filter((e) => {
        if (!isIn(e.dept,       s.department)) return false;
        if (!isIn(e.location,   s.location))   return false;
        if (!isIn(e.city,       s.city))       return false;
        if (!isIn(e.country,    s.country))    return false;
        if (!isIn(e.company,    s.company))    return false;
        if (!isIn(e.state,      s.state))      return false;
        if (!isIn(e.courseName, s.course))     return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        if (q) {
            const hay = `${e.firstName} ${e.lastName} ${e.email} ${e.courseName} ${e.dept} ${e.location} ${e.courseType}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });
}

export function buildScoreKpis(log) {
    const pct = (e) => (e.correctAnswers / e.totalQuestions) * 100;
    const mean = (rows) => (rows.length ? Math.round(rows.reduce((a, e) => a + pct(e), 0) / rows.length) : 0);
    const scoredAttempts = (log || []).filter((e) => e.correctAnswers != null && e.totalQuestions > 0);
    const completedScored = scoredAttempts.filter((e) => e.dateCompleted);
    const passedAttempts = completedScored.filter((e) => pct(e) >= 80);
    return {
        avgScore: mean(scoredAttempts),
        passMarkRate: completedScored.length ? Math.round((passedAttempts.length / completedScored.length) * 100) : 0,
        passedCount: passedAttempts.length,
        completedScoredCount: completedScored.length,
        avgPassScore: mean(passedAttempts),
    };
}

// -- Outstanding users, preview card ------------------------------------
// Lists the top 5 users with the most outstanding course completions.
// Matches the Course Send Log table styling. "View all" routes to the
// full filterable page.
function OutstandingUsersCard({ log = COURSE_SEND_LOG, drill, usersAgg }) {
    const TOP_N = 5;
    const allStats = useMemo(() => (
        Array.isArray(usersAgg)
            ? usersAgg.map((u) => ({
                email: u.email,
                name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                dept: u.dept || 'Unknown',
                outstanding: u.outstanding,
                sent: u.sent,
                oldestDays: u.oldestDays,
              }))
            : buildOutstandingStats(log)
    ), [log, usersAgg]);
    const preview = allStats.slice(0, TOP_N);

    const TH = ({ children }) => (
        <Box component="th" sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled', px: 2, py: 1.5, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
            {children}
        </Box>
    );

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Outstanding Users
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Users with the most incomplete courses</Typography>
                </Box>
                <Button
                    size="small" variant="outlined"
                    onClick={() => drill && drill('outstanding-users', 'Outstanding Users')}
                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                >
                    View all ({allStats.length})
                </Button>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <ViewTH />
                                <TH>Name</TH>
                                <TH>Department</TH>
                                <TH>Outstanding</TH>
                                <TH>Overdue</TH>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {preview.length === 0 && (
                                <Box component="tr">
                                    <Box component="td" colSpan={5} sx={{ px: 2, py: 4, color: 'text.disabled', fontSize: 12, textAlign: 'center' }}>No outstanding courses</Box>
                                </Box>
                            )}
                            {preview.map((u) => (
                                <Box
                                    component="tr"
                                    key={u.email || u.name}
                                    sx={{ borderBottom: '1px solid', borderColor: 'divider', transition: 'background 150ms', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}
                                >
                                    <Box component="td" sx={{ px: 2, py: 1.5, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => drill && drill('user-detail', u.name, { type: 'user', value: u.email || u.name })} />
                                    </Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, whiteSpace: 'nowrap' }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>{u.name}</Typography>
                                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{u.email}</Typography>
                                    </Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{u.dept}</Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: 'warning.main' }}>
                                        {u.outstanding}
                                        <Box component="span" sx={{ fontSize: 10, fontWeight: 500, color: 'text.disabled', ml: 0.5 }}>
                                            / {u.sent}
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 600, color: u.oldestDays >= 30 ? 'var(--red)' : u.oldestDays >= 14 ? 'var(--amber)' : 'text.secondary' }}>
                                        {u.oldestDays}d
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </MainCard>
    );
}

// Aligned with the Course Send History page: same canonical column set
// (status/outcome badges, score, local-time stamps), newest 10 events, row
// click drills to the user, View all lands on the full history page.
function CourseSendLogCard({ drill, log = COURSE_SEND_LOG }) {
    // The log is sorted newest-first, so the head is the last 10 events.
    const preview = log.slice(0, 10);

    return (
        <DataTable
            title="Course Send History"
            subtitle="Last 10 delivery events"
            columns={courseLogColumns()}
            rows={preview}
            rowKey={(e) => `${e.email}-${e.courseName}-${e.sentAt || e.dateSent}`}
            onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
            initialSort={{ by: 'sent', dir: 'desc' }}
            storageKey="rbx-courses-send-history-card-cols"
            pagination={false}
            emptyMessage="No course delivery events in scope"
            headerActions={
                <Button size="small" variant="outlined" onClick={() => drill('course-send-log', 'Course Send History', null)}
                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}>
                    View all ({log.length.toLocaleString()})
                </Button>
            }
        />
    );
}

export default function CoursesPage({ go, drill, period, startDate, endDate, scope, selfScope, removeScope, search = '', vis: visProp, orderedGroup: orderedGroupProp }) {
    const theme = useTheme();
    const vis = visProp || (() => true);
    // Fallback for standalone render (tests). Keys match the section titles
    // in widgetRegistry.js so renames flow through unchanged.
    const orderedGroup = orderedGroupProp || ((group) => {
        const defaults = {
            'Performance':          ['metric-courses-sent', 'metric-completion-rate', 'metric-avg-score', 'metric-pass-mark-rate', 'metric-pass-rate', 'metric-training-debt', 'metric-course-lag'],
            'Engagement over time': ['sends-vs-completions'],
            'Action needed':        ['outstanding-users-card'],
            'Completion breakdown': ['by-type-completion', 'by-dept-completion', 'dept-by-type-heatmap'],
            'Score quality':        ['score-marimekko', 'score-by-type-marimekko'],
            'Detail tables':        ['all-courses', 'users-table'],
            'Activity log':         ['course-send-log-card'],
        };
        return (defaults[group] || []).map((id) => ({ id }));
    });

    // One canonical scope+search-filtered log for the whole page. Every chart
    // that aggregates against COURSE_SEND_LOG receives this so the dashboard
    // reacts as filters or search change. Scope values are arrays (filter
    // stacking), so a row passes if its field is in the value array, or if
    // there's no filter for that dimension at all.
    const scopedLog = useMemo(() => filterCourseLog(COURSE_SEND_LOG, scope, search), [scope, search]);

    // Deliveries IN THE SELECTED PERIOD, on the same window the server queries use,
    // for the KPI fallbacks, the tables and the Course Send History card -
    // scopedLog itself stays unwindowed for the widgets that window internally.
    const windowedLog = useMemo(() => logInPeriod(scopedLog, { period, startDate, endDate }), [scopedLog, period, startDate, endDate]);
    const tenancyCourseEvents = useTenancyCourseEvents({ scope, selfScope, period, startDate, endDate });
    const sendLogRows = useMemo(
        () => (tenancyCourseEvents.scoped ? logInPeriod(filterCourseLog(tenancyCourseEvents.rows || [], scope, search), { period, startDate, endDate }) : windowedLog),
        [tenancyCourseEvents.scoped, tenancyCourseEvents.rows, scope, search, period, startDate, endDate, windowedLog],
    );

    // Proper-fix: headline KPIs for the unscoped view come from the server's
    // precomputed, period-windowed course_detail aggregate (uncapped, consistent).
    // When a scope filter is active we still compute from scopedLog (scope-aware).
    const courseScopeActive = (() => { const sc = scope || {}; return Object.keys(sc).some((key) => { const v = sc[key]; return Array.isArray(v) ? v.length > 0 : !!v; }); })();
    const sBody = scope || {};
    const serverScope = toServerScope(sBody);
    const hasServerScope = !!serverScope;
    const clientOnlyScope = !!(sBody.course || (search && String(search).trim()));
    const courseDetailQ = useDashboardData({ metric: 'courses', aggregation: 'course_detail', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const cdKpis = (!clientOnlyScope && courseDetailQ.data?.courseDetail?.kpis) || null;
    const coursesWindowedQ = useDashboardData({ metric: 'courses', aggregation: 'courses', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const coursesAggData = (!clientOnlyScope && Array.isArray(coursesWindowedQ.data?.courses)) ? coursesWindowedQ.data.courses : null;
    const scoreRows = useMemo(() => coursesAggData || courseScoreRows(windowedLog), [coursesAggData, windowedLog]);
    const trendsQ = useDashboardData({ metric: 'courses', aggregation: 'trends', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const trendsAggData = (!clientOnlyScope && Array.isArray(trendsQ.data?.trends)) ? trendsQ.data.trends : null;
    const lagQ = useDashboardData({ metric: 'courses', aggregation: 'course_lag', period, startDate, endDate, scope: (hasServerScope && !clientOnlyScope) ? serverScope : undefined });
    const lagAggData = (!clientOnlyScope && Array.isArray(lagQ.data?.courseLag)) ? lagQ.data.courseLag : null;
    const serverCourseUsers = useCourseUsers({ scope, period, startDate, endDate }, { skip: clientOnlyScope || !vis('users-table') });
    const serverCourseUsersQ = useCourseUsersQuery({ scope, period, startDate, endDate }, { skip: clientOnlyScope || !vis('users-table') });

    // Filter pickers: courses from the hydrated catalogue, users by name + email.
    // Mirrors the Simulations page pickers (course scope is already client-only
    // here, so no server-scope handling changes). Already-scoped values are
    // hidden so a pick always changes the filter.
    // Course / User filter chips render in the shell's dimension-chip row (MetaNav).

    const totalSent = windowedLog.length;
    const totalCompleted = windowedLog.filter((e) => e.dateCompleted).length;
    const overallPct = totalSent === 0 ? '0.0' : ((totalCompleted / totalSent) * 100).toFixed(1);

    // Training debt = courses past their due date and still incomplete.
    // Mirrors the rule used in riskScore.js so the "Training debt" factor
    // on Home and this KPI tell the same story.
    const COURSE_DUE_DAYS = 14;
    const trainingDebt = useMemo(() => {
        const todayMs = Date.now(); // live wall clock (was pinned to the mock dataset anchor)
        return windowedLog.filter((e) => {
            if (e.dateCompleted) return false;
            const sentMs = Date.parse(e.dateSent + 'T00:00:00Z');
            const ageDays = (todayMs - sentMs) / 86400000;
            return ageDays > COURSE_DUE_DAYS;
        }).length;
    }, [windowedLog]);

    // Course lag = mean days from send to completion across completed
    // assignments. Caps at 30 days per row to match the score's lag
    // signal definition.
    const courseLagDays = useMemo(() => {
        // Matches the score model: every assignment counts - completed ones at
        // their send->complete time, still-open ones at how long they have been
        // outstanding - each capped at 30 days.
        if (windowedLog.length === 0) return 0;
        const todayMs = Date.now();
        const total = windowedLog.reduce((sum, e) => {
            const sentMs = Date.parse(e.dateSent + 'T00:00:00Z');
            const endMs = e.dateCompleted ? Date.parse(e.dateCompleted + 'T00:00:00Z') : todayMs;
            return sum + Math.min(Math.max(0, (endMs - sentMs) / 86400000), 30);
        }, 0);
        return +(total / windowedLog.length).toFixed(1);
    }, [windowedLog]);

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
                </Box>
            </Box>

            {/* Performance, top-line training KPIs. Now also surfaces the
                two score-driver factors that live on this page (Training
                debt and Course lag) as headline numbers so the link from
                Home's score driver lands on a visible metric, not just a
                table of users. */}
            {(vis('metric-courses-sent') || vis('metric-completion-rate') || vis('metric-avg-score') || vis('metric-pass-mark-rate') || vis('metric-pass-rate') || vis('metric-training-debt') || vis('metric-course-lag')) && (() => {
                const exploreFactor = (factorId, factorLabel) =>
                    drill && drill('factor-detail', factorLabel, { type: 'factor', value: factorId });
                const { avgScore, passMarkRate, passedCount, completedScoredCount, avgPassScore } =
                    cdKpis && cdKpis.avgScore != null ? cdKpis : buildScoreKpis(cdKpis && hasTenancyScope(scope) ? [] : windowedLog);
                const cards = [
                    vis('metric-courses-sent')     && { key: 'courses-sent',    label: 'Courses Sent',  value: (cdKpis ? cdKpis.sent : totalSent).toLocaleString(), sub: `${(cdKpis ? cdKpis.completed : totalCompleted).toLocaleString()} of ${(cdKpis ? cdKpis.sent : totalSent).toLocaleString()} completed`,
                        actionLabel: 'More', onAction: () => drill && drill('course-send-log', 'Course Send History') },
                    vis('metric-completion-rate')  && { key: 'completion',      label: 'Completion Rate', value: cdKpis ? String(cdKpis.completionPct) : overallPct, unit: '%', sub: `${(cdKpis ? cdKpis.completed : totalCompleted).toLocaleString()} completions`,
                        actionLabel: 'More', onAction: () => drill && drill('course-send-log', 'Completed sends', { type: 'status', value: 'Completed' }) },
                    vis('metric-avg-score')        && { key: 'avg-score',       label: 'Avg Score', value: String(avgScore), unit: '%', sub: 'Across course attempts in the period' },
                    vis('metric-pass-mark-rate')   && { key: 'pass-mark-rate',  label: 'Pass Rate', value: String(passMarkRate), unit: '%', sub: `${passedCount.toLocaleString()} of ${completedScoredCount.toLocaleString()} scored 80+` },
                    vis('metric-pass-rate')        && { key: 'pass-rate',       label: 'Avg Pass Score', value: String(avgPassScore), unit: '%', sub: 'Average Score where pass mark was achieved' },
                    vis('metric-training-debt')    && {
                        key: 'training-debt', label: 'Training Debt', value: (cdKpis ? cdKpis.trainingDebt : trainingDebt).toLocaleString(), sub: 'Overdue assignments still incomplete',
                        actionLabel: 'More', onAction: () => exploreFactor('training-debt', 'Training debt'),
                    },
                    vis('metric-course-lag')       && {
                        key: 'course-lag', label: 'Avg Course Lag', value: cdKpis ? cdKpis.courseLag : courseLagDays, unit: 'd', sub: 'All assignments, open ones at age - cap 30d',
                        actionLabel: 'More', onAction: () => exploreFactor('course-lag', 'Course lag'),
                    },
                ].filter(Boolean);
                if (cards.length === 0) return null;
                return (
                    <>
                        <SectionTitle>Performance</SectionTitle>
                        <motion.div variants={fadeUp}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 1,
                                    width: '100%',
                                    // Cap at 4 per row so seven KPIs spread over two lines
                                    // instead of being squeezed onto one, matching Sims.
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        md: `repeat(${Math.min(4, cards.length)}, minmax(0, 1fr))`,
                                    },
                                    alignItems: 'stretch',
                                }}
                            >
                                {cards.map((c) => (
                                    <Box key={c.key} sx={{ minWidth: 0, '& > *': { width: '100%', height: '100%' } }}>
                                        <WidgetState queries={[courseDetailQ]} fill><MetricCard label={c.label} value={c.value} unit={c.unit} sub={c.sub} actionLabel={c.actionLabel} onAction={c.onAction} /></WidgetState>
                                    </Box>
                                ))}
                            </Box>
                        </motion.div>
                    </>
                );
            })()}

            {/* Engagement over time: Sends vs Completions and Course
                Lag share a row at 50/50 so the reader sees "are people
                completing" and "how long are they taking" side by side.
                If one is hidden the survivor takes the full row; on
                narrow viewports the Grid stacks them. */}
            {(vis('sends-vs-completions') || vis('course-lag-chart')) && (
                <>
                    <SectionTitle>Engagement over time</SectionTitle>
                    <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex', minWidth: 0 } }}>
                        {vis('sends-vs-completions') && (
                            <Grid item xs={12} md={vis('course-lag-chart') ? 6 : 12}>
                                <motion.div variants={fadeUp} style={{ flex: 1, display: 'flex', minWidth: 0, width: '100%' }}>
                                    <Box sx={{ height: 360, width: '100%', display: 'flex' }}>
                                        <WidgetState queries={[trendsQ]} fill><CompletionsByMonthChart period={period} startDate={startDate} endDate={endDate} theme={theme} log={scopedLog} trendsAgg={trendsAggData} /></WidgetState>
                                    </Box>
                                </motion.div>
                            </Grid>
                        )}
                        {vis('course-lag-chart') && (
                            <Grid item xs={12} md={vis('sends-vs-completions') ? 6 : 12}>
                                <motion.div variants={fadeUp} style={{ flex: 1, display: 'flex', minWidth: 0, width: '100%' }}>
                                    <Box sx={{ height: 360, width: '100%', display: 'flex' }}>
                                        <WidgetState queries={[lagQ]} fill><CourseLagChart log={windowedLog} lagAgg={serverRowsOrNull(lagAggData, scope)} /></WidgetState>
                                    </Box>
                                </motion.div>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {/* Action needed, Outstanding users sits high because
                "who still has training open" is the most actionable
                view on the page; pushing it up the rail makes it the
                first thing a returning admin sees. Full-width so the
                table can stretch and surface enough rows without
                competing with the send log. */}
            {vis('outstanding-users-card') && (
                <>
                    <SectionTitle>Action needed</SectionTitle>
                    <motion.div variants={fadeUp}>
                        <WidgetState queries={[courseDetailQ]}><OutstandingUsersCard drill={drill} log={windowedLog} usersAgg={!clientOnlyScope ? courseDetailQ.data?.courseDetail?.outstandingUsers : null} /></WidgetState>
                    </motion.div>
                </>
            )}

            {/* Completion breakdown. Completion-by-Group sits up top
                (full-width when alone), then the Department by Type
                heatmap full-width. The standalone Completion-by-Type
                chart was removed: its first row was the same data the
                Department by Type heatmap already shows as a column
                summary, so it duplicated the message. */}
            {(vis('by-dept-completion') || vis('dept-by-type-heatmap')) && (
                <>
                    <SectionTitle>Completion breakdown</SectionTitle>
                    {vis('by-dept-completion') && (
                        <motion.div variants={fadeUp}>
                            <WidgetState queries={[courseDetailQ]}><CompletionByDeptChart log={windowedLog} period={period} startDate={startDate} endDate={endDate} drill={drill} scope={scope} removeScope={removeScope} deptAgg={!clientOnlyScope ? courseDetailQ.data?.courseDetail?.byDept : null} orgAgg={cdKpis} useAgg={!clientOnlyScope} serverScope={(hasServerScope && !clientOnlyScope) ? serverScope : undefined} /></WidgetState>
                        </motion.div>
                    )}
                    {vis('dept-by-type-heatmap') && (
                        <motion.div variants={fadeUp}><DeptByTypeHeatmap log={windowedLog} period={period} startDate={startDate} endDate={endDate} drill={drill} scope={scope} removeScope={removeScope} useAgg={!clientOnlyScope} serverScope={(hasServerScope && !clientOnlyScope) ? serverScope : undefined} /></motion.div>
                    )}
                </>
            )}

            {/* Score quality, marimekko per course and per type, side
                by side at 50/50 with pagination so each card shows at
                most six bars at a time. Either card can disappear and
                the surviving one expands to full width. */}
            {(vis('score-marimekko') || vis('score-by-type-marimekko')) && (
                <>
                    <SectionTitle>Score quality</SectionTitle>
                    <motion.div variants={fadeUp}>
                        <Grid container spacing={1} alignItems="stretch">
                            {vis('score-marimekko') && (
                                <Grid item xs={12} md={vis('score-by-type-marimekko') ? 6 : 12} sx={{ display: 'flex', minWidth: 0 }}>
                                    <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
                                        <WidgetState queries={[coursesWindowedQ]} fill><ScoreBreakdownMarimekko theme={theme} coursesData={scoreRows} /></WidgetState>
                                    </Box>
                                </Grid>
                            )}
                            {vis('score-by-type-marimekko') && (
                                <Grid item xs={12} md={vis('score-marimekko') ? 6 : 12} sx={{ display: 'flex', minWidth: 0 }}>
                                    <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
                                        <WidgetState queries={[coursesWindowedQ]} fill><ScoreBreakdownByTypeMarimekko coursesData={scoreRows} /></WidgetState>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </motion.div>
                </>
            )}

            {/* Detail tables: All Courses and Users, mirroring the Simulations
                page's detail cards - top 10 previews with View-all drills. */}
            {(vis('all-courses') || vis('users-table')) && <SectionTitle>Detail tables</SectionTitle>}
            {vis('all-courses') && (() => {
                const allCourseRows = (coursesAggData ? courseRowsFromAgg(coursesAggData) : buildCourseRows(windowedLog)).sort((a, b) => b.sent - a.sent);
                const COURSES_TOP_N = 10;
                const topCourses = allCourseRows.slice(0, COURSES_TOP_N);
                return (
                    <>
                        <motion.div key="all-courses" variants={fadeUp}>
                            <WidgetState queries={[coursesWindowedQ]}>
                            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                                <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>All Courses</Typography>
                                        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Top {Math.min(COURSES_TOP_N, allCourseRows.length)} of {allCourseRows.length} by sends - click View for detail</Typography>
                                    </Box>
                                    {allCourseRows.length > COURSES_TOP_N && (
                                        <Button
                                            size="small" variant="outlined"
                                            onClick={() => drill('all-courses-table', 'All Courses')}
                                            sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                                        >
                                            View all ({allCourseRows.length})
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                                    <TableHeader columns={['Course', 'Sends', 'Completed', 'Outstanding']} template="1fr 90px 100px 110px" actions />
                                    <motion.div variants={stagger} initial="hidden" animate="visible">
                                        {topCourses.map((c) => (
                                            <motion.div key={c.name} variants={fadeUp}>
                                                <TableRow template="1fr 90px 100px 110px" onClick={() => drill('course-detail', c.name, { type: 'course', value: c.name })}>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>{c.name}</Typography>
                                                        {c.type && <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{c.type}</Typography>}
                                                    </Box>
                                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{c.sent.toLocaleString()}</Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#66BB6A' }}>{c.completed.toLocaleString()}</Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.outstanding > 0 ? '#FFCA28' : 'text.secondary' }}>{c.outstanding.toLocaleString()}</Typography>
                                                </TableRow>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </Box>
                            </MainCard>
                            </WidgetState>
                        </motion.div>
                    </>
                );
            })()}

            {/* Users: top 10 by worst course completion, mirroring the sims
                Users card. View all lands on the Users page ordered by
                completion rate ascending with the page scope carried. */}
            {vis('users-table') && (() => {
                const allUsers = (!clientOnlyScope && serverCourseUsers) ? serverCourseUsers : (() => {
                    const byUser = new Map();
                    for (const e of windowedLog) {
                        const cur = byUser.get(e.email) || { email: e.email, name: `${e.firstName} ${e.lastName}`.trim() || e.email, sent: 0, completed: 0 };
                        cur.sent += 1;
                        if (e.dateCompleted) cur.completed += 1;
                        byUser.set(e.email, cur);
                    }
                    return [...byUser.values()].map((u) => ({
                        ...u,
                        outstanding: u.sent - u.completed,
                        completionRate: u.sent ? Math.round((u.completed / u.sent) * 100) : 0,
                    }));
                })();
                const USERS_TOP_N = 10;
                const worstUsers = [...allUsers]
                    .sort((a, b) => (a.completionRate - b.completionRate) || (b.outstanding - a.outstanding))
                    .slice(0, USERS_TOP_N);
                const userCols = [
                    { id: 'name',           label: 'Name',         always: true,    sortable: true, type: 'string', accessor: (u) => u.name },
                    { id: 'email',          label: 'Email',        defaultOn: true, sortable: true, type: 'string', accessor: (u) => u.email },
                    { id: 'sent',           label: 'Sends',        defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
                    { id: 'completed',      label: 'Completed',    defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.completed, align: 'right' },
                    { id: 'outstanding',    label: 'Outstanding',  defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.outstanding, align: 'right' },
                    { id: 'completionRate', label: 'Completion %', defaultOn: true, sortable: true, type: 'number', accessor: (u) => u.completionRate, align: 'right',
                      render: (u) => <Box component="span" sx={{ fontWeight: 700, color: u.completionRate >= 80 ? '#66BB6A' : u.completionRate >= 50 ? '#FFCA28' : '#FF5252' }}>{u.completionRate}%</Box> },
                ];
                return (
                    <motion.div key="users-table" variants={fadeUp}>
                        <WidgetState queries={[serverCourseUsersQ]}>
                        <DataTable
                            title="Users"
                            subtitle={`Top ${Math.min(USERS_TOP_N, allUsers.length)} of ${allUsers.length} by worst completion - click View for the activity log`}
                            columns={userCols}
                            rows={worstUsers}
                            rowKey={(u) => u.email}
                            onRowClick={(u) => drill('user-detail', u.name, { type: 'user', value: u.email })}
                            initialSort={{ by: 'completionRate', dir: 'asc' }}
                            storageKey="rbx-courses-users-card-cols"
                            pagination={false}
                            emptyMessage="No users in scope"
                            headerActions={allUsers.length > USERS_TOP_N ? (
                                <Button
                                    size="small" variant="outlined"
                                    onClick={() => drill('users', 'Users', { type: 'sort', value: 'completion:asc' })}
                                    sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 10, py: '3px', px: '8px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                                >
                                    View all ({allUsers.length})
                                </Button>
                            ) : null}
                        />
                        </WidgetState>
                    </motion.div>
                );
            })()}

            {/* Activity log, full-width chronological feed of recent
                course deliveries. Stands alone at the bottom of the
                page because it's reference-level detail, not a top
                decision-making surface. */}
            {vis('course-send-log-card') && (
                <>
                    <SectionTitle>Activity log</SectionTitle>
                    <motion.div variants={fadeUp}>
                        <WidgetState queries={[tenancyCourseEvents]}><CourseSendLogCard drill={drill} log={sendLogRows} /></WidgetState>
                    </motion.div>
                </>
            )}

        </motion.div>
    );
}
